import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { APIError, Language, Namespace } from '@data';
import {
  useCreateQuestionTranslations,
  useEditQuestion,
  useQuestionDependencies,
  useQuestionDetails as useQuestionDetailsQuery,
  useQuestionTranslations,
} from '@data/hooks';
import { QuestionType, QuestionUpdateBody, VisibilityType } from '@data/sdk/match';
import { AntdForm, antdNotification } from '@ui/components';
import { getInitialValues, transformQuestionFormValues } from '../utils';

export type QuestionFormValues = {
  expiry: number | null;
  questionType: QuestionType;
  text: {
    [key in Language]: string;
  };
  unit: string;
  visibilityType: VisibilityType;
};

export const useQuestionDetails = () => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const { questionId = '' } = useParams<{ questionId: string }>();

  const { data: question, isLoading: isQuestionLoading } = useQuestionDetailsQuery(questionId);
  const { data: translations } = useQuestionTranslations(questionId);
  const { data: dependencies } = useQuestionDependencies(questionId);

  const [form] = AntdForm.useForm<QuestionFormValues>();
  const [currentLanguage, setCurrentLanguage] = useState<Language>(Language.EN);

  const { mutate: editQuestion } = useEditQuestion();
  const { mutateAsync: createTranslations } = useCreateQuestionTranslations();

  const initialValues: QuestionFormValues = getInitialValues(question, translations?.items);

  const text = AntdForm.useWatch('text', form);
  const visibilityType = AntdForm.useWatch('visibilityType', form);
  const questionType = AntdForm.useWatch('questionType', form);
  const unit = AntdForm.useWatch('unit', form);
  const expiry = AntdForm.useWatch('expiry', form);
  const currentLanguageQuestionText = AntdForm.useWatch(['text', currentLanguage], form);
  const enQuestionText = form.getFieldValue(['text', Language.EN]);
  const questionText = currentLanguageQuestionText || enQuestionText;

  const isFormDirty = useMemo(() => {
    const isVisibilityTypeDirty = visibilityType !== initialValues.visibilityType;
    const isTranslationsDirty = Object.keys(text || {}).some(
      (key) => text[key as Language] !== initialValues.text[key as Language],
    );
    const isQuestionTypeDirty = questionType !== initialValues.questionType;
    const isUnitDirty = unit !== initialValues.unit;
    const isExpiryDirty = expiry !== initialValues.expiry;

    return {
      isQuestionTypeDirty,
      isTranslationsDirty,
      isVisibilityTypeDirty,
      isExpiryDirty,
      isDirty:
        isVisibilityTypeDirty ||
        isTranslationsDirty ||
        isQuestionTypeDirty ||
        isUnitDirty ||
        isExpiryDirty,
    };
  }, [initialValues, text, visibilityType, unit, expiry]);

  const handleResetForm = () => {
    form.resetFields();
  };

  const onError = (err: APIError<QuestionUpdateBody>) =>
    antdNotification.error({
      message: t(`patient:beErrors.${err.response?.data.message}`, {
        defaultValue: t('patient:beErrors.unexpectedError'),
        item: t('question_page.details.form.question'),
      }),
    });

  const handleEditQuestion = async (values: QuestionFormValues) => {
    if (!question) return;

    const { translationsValues, questionValues } = transformQuestionFormValues(values);

    editQuestion(
      { id: question.id, values: questionValues },
      {
        onSuccess: async () => {
          if (isFormDirty.isTranslationsDirty) {
            await createTranslations({
              id: question.id,
              values: { translations: translationsValues },
            });
          }
        },
        onError,
      },
    );
  };

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [question, translations?.items]);

  return {
    dependencies,
    translations,
    questionHelpers: {
      form,
      question,
      isQuestionLoading,
      currentLanguage,
      initialValues,
      visibilityType,
      questionText,
      isFormDirty: isFormDirty.isDirty,
      handleEditQuestion,
      handleResetForm,
      setCurrentLanguage,
    },
  };
};
